<template>
    <el-container direction="vertical" class="payrolls">
        <div class="header">
            <el-row>
                <el-col>
                    <div class="title">
                        {{ title }}
                    </div>
                </el-col>
            </el-row>
        </div>
        <div class="main" v-loading="loading">
            <div class="control-grid">
                <div class="filter-label">
                    Год
                </div>
                <el-select
                    v-model="filter.report_year"
                    placement="bottom"
                    popper-class="popper-dropdown"
                    placeholder="Год"
                    size="mini"
                >
                    <el-option v-for="item in localReferences.yearsList" :key="item" :label="item" :value="item" />
                </el-select>
                <div class="filter-label">
                    Месяц
                </div>
                <el-select
                    v-model="filter.report_month"
                    placement="bottom"
                    popper-class="popper-dropdown"
                    clearable
                    placeholder="Месяц"
                    size="mini"
                >
                    <el-option v-for="item in localReferences.monthsList" :key="item" :label="item" :value="item" />
                </el-select>
                <div class="filter-label">
                    Сотрудник
                </div>
                <el-select
                    v-model="filter.employer_id"
                    placement="bottom"
                    popper-class="popper-dropdown"
                    filterable
                    multiple
                    collapse-tags
                    clearable
                    size="mini"
                >
                    <el-option v-for="item in employersList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <el-button type="warning" @click="getPayrolls">
                    Поиск
                </el-button>
                <el-button type="warning" @click="addNewPayroll">
                    Добавить
                </el-button>
                <el-button type="warning" @click="showCloneForm" class="clone-form-action">
                    <span class="clone-form-action">Клонировать период</span>
                </el-button>
            </div>
            <div class="payrolls-grid">
                <template v-for="(column, index) in payrollColumns">
                    <div :key="'header_' + index" class="header-cell" :title="column.hint">
                        {{ column.headerName }}
                    </div>
                </template>
                <div class="header-cell">
                    &nbsp;
                </div>
                <template v-for="(payroll, payrollIndex) in payrolls">
                    <template v-for="(column, columnIndex) in payrollColumns">
                        <div
                            :key="payrollIndex+ '_'+ columnIndex"
                            :class="column.fieldClass"
                            :title="payroll[column.field]"
                        >
                            <template v-if="['salary', 'total'].includes(column.field)">
                                {{ formatFinance(payroll[column.field]) }}
                            </template>
                            <template v-else-if="column.field === 'cashless'">
                                {{ formatFinance(payroll[column.field]) }}
                            </template>
                            <template v-else-if="['cash', 'fine', 'bonus'].includes(column.field)">
                                <template v-if="payroll.editMode">
                                    <auto-numeric
                                        v-model="payroll[column.field]"
                                        @input="handleNumericInput(payroll.id, column.field)"
                                        :options="[numericOptionsGeneral, column.field === 'fine' ? numericOptionsNegative : numericOptionsPositive]"
                                        :class="['autonumeric', {'validation-failed': payroll.errors && payroll.errors[column.field]}]"
                                    />
                                </template>
                                <template v-else>
                                    {{ formatFinance(payroll[column.field]) }}
                                </template>
                            </template>
                            <template v-else-if="column.field === 'report_year'">
                                <template v-if="payroll.editMode">
                                    <el-select
                                        v-model="payroll.report_year"
                                        placement="bottom"
                                        popper-class="popper-dropdown"
                                        placeholder="Год"
                                        size="mini"
                                        @change="handleReportYearChange(payroll.id)"
                                        :class="{'validation-failed': payroll.errors && payroll.errors.report_year}"
                                    >
                                        <el-option v-for="item in localReferences.yearsList" :key="item" :label="item" :value="item" />
                                    </el-select>
                                </template>
                                <template v-else>
                                    {{ payroll[column.field] }}
                                </template>
                            </template>
                            <template v-else-if="column.field === 'report_month'">
                                <template v-if="payroll.editMode">
                                    <el-select
                                        v-model="payroll.report_month"
                                        placement="bottom"
                                        popper-class="popper-dropdown"
                                        placeholder="Месяц"
                                        size="mini"
                                        @change="handleReportMonthChange(payroll.id)"
                                        :class="{'validation-failed': payroll.errors && payroll.errors.report_month}"
                                    >
                                        <el-option v-for="item in localReferences.monthsList" :key="item" :label="item" :value="item" />
                                    </el-select>
                                </template>
                                <template v-else>
                                    {{ payroll[column.field] }}
                                </template>
                            </template>
                            <template v-else-if="column.field === 'employer_name'">
                                <template v-if="payroll.editMode">
                                    <el-select
                                        v-model="payroll.employer_id"
                                        placement="bottom"
                                        popper-class="popper-dropdown"
                                        filterable
                                        @change="handleEmployerIdChange(payroll.id)"
                                        size="mini"
                                        :class="{'validation-failed': payroll.errors && payroll.errors.employer_id}"
                                    >
                                        <el-option v-for="item in employersList" :key="item.id" :label="item.name" :value="item.id" />
                                    </el-select>
                                </template>
                                <template v-else>
                                    {{ payroll[column.field] }}
                                </template>
                            </template>
                            <template v-else-if="column.field === 'id'">
                                {{ payroll.isNew ? '' : payroll[column.field] }}
                            </template>
                            <template v-else>
                                {{ payroll[column.field] }}
                            </template>
                        </div>
                    </template>
                    <div :key="payrollIndex+ '_'+ 13" class="grid-cell">
                        <template v-if="payroll.editMode">
                            <font-awesome-icon icon="save" class="action-icon" @click="savePayroll(payroll.id)" />
                            <font-awesome-icon icon="undo-alt" class="action-icon" @click="cancelChanges(payroll.id)" />
                        </template>
                        <template v-else>
                            <font-awesome-icon icon="edit" class="action-icon" @click="enableEditMode(payroll.id)" />
                            <font-awesome-icon icon="trash-alt" class="action-icon" @click="handleDeleteButtonClick(payroll.id)" />
                        </template>
                    </div>
                </template>
            </div>
        </div>
        <el-pagination
            background
            layout="prev, pager, next"
            :total="pages.total"
            :page-size="pages.size"
            :current-page="pages.current"
            :small="true"
            :hide-on-single-page="true"
            class="mt10"
            @current-change="handleChangePage"
        />
        <clone-form
            :is-visible="isCloneFormVisible"
            @hideForm="hideCloneForm"
            @refreshList="getPayrolls"
        />
    </el-container>
</template>

<script>
import AutoNumeric from '@/components/autonumeric/VueAutonumeric';
import CloneForm from '@/components/salary/PayrollCloneForm';
import moment from 'moment';
import localReferences from '@/helpers/localReferences';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSave, faEdit, faTrashAlt, faUndoAlt } from '@fortawesome/free-solid-svg-icons';
import { formatFinance } from '@/common/utilities';
import '@/assets/styles/expenses.scss';

library.add(faSave, faEdit, faTrashAlt, faUndoAlt);

export default {
    name: 'EmployerPayroll',
    components: { AutoNumeric, CloneForm },
    data() {
        return {
            localReferences,
            title: 'Зарплатная ведомость',
            loading: false,
            payrollColumns: [
                { headerName: 'id', hint: 'Id', field: 'id', fieldClass: 'grid-cell' },
                { headerName: 'Создано', hint: 'Создано', field: 'created_at', fieldClass: 'grid-cell' },
                { headerName: 'Создатель', hint: 'Создатель', field: 'creator_name', fieldClass: 'grid-cell' },
                { headerName: 'Год', hint: 'Год (отчетного периода)', field: 'report_year', fieldClass: 'grid-cell' },
                { headerName: 'Месяц', hint: 'Месяц (отчетного периода)', field: 'report_month', fieldClass: 'grid-cell' },
                { headerName: 'Сотрудник', hint: 'Сотрудник', field: 'employer_name', fieldClass: 'grid-cell' },
                { headerName: 'На карту ', hint: 'На карту ', field: 'cashless', fieldClass: 'grid-cell' },
                { headerName: 'В руки', hint: 'В руки', field: 'cash', fieldClass: 'grid-cell' },
                { headerName: 'Оклад', hint: 'Оклад', field: 'salary', fieldClass: 'grid-cell' },
                { headerName: 'Штраф', hint: 'Штраф', field: 'fine', fieldClass: 'grid-cell' },
                { headerName: 'Бонус', hint: 'Бонус', field: 'bonus', fieldClass: 'grid-cell' },
                { headerName: 'Итого', hint: 'Итого', field: 'total', fieldClass: 'grid-cell' }
            ],
            numericOptionsGeneral: {
                digitGroupSeparator: ' ',
                decimalCharacter: ',',
                decimalPlaces: 0,
                decimalCharacterAlternative: '.',
                roundingMethod: 'U',
                isCancellable: true,
                emptyInputBehavior: '0',
                modifyValueOnWheel: false
            },
            numericOptionsPositive: {
                minimumValue: '0',
                maximumValue: '9999999'
            },
            numericOptionsNegative: {
                minimumValue: '-9999999',
                maximumValue: '0'
            },
            payrolls: [],
            uneditedPayrolls: [],
            filter: {
                report_year: new Date().getFullYear(),
                report_month: null,
                employer_id: []
            },
            isCloneFormVisible: false,
            pages: {
                current: 1,
                total: 1,
                size: 40
            }
        };
    },
    computed: {
        employersList() {
            return this.$store.state.references.employers;
        }
    },
    methods: {
        formatFinance,
        findPayroll(payrollId) {
            return this.payrolls.find(payroll => payroll.id === +payrollId);
        },
        enableEditMode(payrollId) {
            const payroll = this.findPayroll(payrollId);
            if (payroll) {
                this.uneditedPayrolls.push(Object.assign({}, payroll));
                payroll.editMode = true;
            }
        },
        cancelChanges(payrollId) {
            const payroll = this.findPayroll(payrollId);
            if (payroll && payroll.isNew) {
                this.payrolls.splice(this.payrolls.findIndex(payroll => payroll.id === +payrollId), 1);
                return;
            }
            const uneditedPayroll = this.uneditedPayrolls.find(payroll => payroll.id === +payrollId);
            if (uneditedPayroll && payroll) {
                for (const field in uneditedPayroll) {
                    if (Object.prototype.hasOwnProperty.call(uneditedPayroll, field) && Object.prototype.hasOwnProperty.call(payroll, field)) {
                        payroll[field] = uneditedPayroll[field];
                    }
                }
                this.uneditedPayrolls.splice(this.uneditedPayrolls.findIndex(payroll => payroll.id === +payrollId), 1);
            }
        },
        handleNumericInput(payrollId, field) {
            const payroll = this.findPayroll(payrollId);
            if (payroll) {
                payroll.salary = payroll.cash + payroll.cashless;
                payroll.total = payroll.cash + payroll.cashless + payroll.bonus + payroll.fine;
                if (payroll.errors && payroll.errors[field]) {
                    delete payroll.errors[field];
                }
            }
        },
        handleEmployerIdChange(payrollId) {
            const payroll = this.findPayroll(payrollId);
            if (payroll && payroll.employer_id) {
                payroll.employer_name = this.employersList.find(employer => employer.id === payroll.employer_id).name;
                if (payroll.errors && payroll.errors.employer_id) {
                    delete payroll.errors.employer_id;
                }
            }
        },
        handleReportYearChange(payrollId) {
            const payroll = this.findPayroll(payrollId);
            if (payroll && payroll.report_year && payroll.errors && payroll.errors.report_year) {
                delete payroll.errors.report_year;
            }
        },
        handleReportMonthChange(payrollId) {
            const payroll = this.findPayroll(payrollId);
            if (payroll && payroll.report_month && payroll.errors && payroll.errors.report_month) {
                delete payroll.errors.report_month;
            }
        },
        showCloneForm() {
            this.isCloneFormVisible = !this.isCloneFormVisible;
        },
        hideCloneForm() {
            this.isCloneFormVisible = false;
        },
        handleChangePage(page) {
            this.pages.current = page;
            this.getPayrolls();
        },
        addNewPayroll() {
            const newPayroll = {
                id: +('' + Date.now() + '00000000'),
                created_at: moment().format('DD.MM.YYYY HH:mm'),
                created_by_id: this.$store.state.auth.employer.id,
                creator: {},
                creator_name: this.$store.state.auth.employer.name,
                employer_id: null,
                employer: {},
                employer_name: '',
                report_date: null,
                report_month: this.filter.report_month,
                report_year: this.filter.report_year,
                bonus: 0,
                cash: 0,
                cashless: 0,
                fine: 0,
                salary: 0,
                total: 0,
                editMode: true,
                isNew: true
            };
            this.payrolls.unshift(newPayroll);
        },
        async savePayroll(payrollId) {
            const payroll = this.findPayroll(payrollId);
            if (payroll) {
                payroll.report_date = `01.${payroll.report_month}.${payroll.report_year}`;
                const action = payroll.isNew ? 'createPayroll' : 'updatePayroll';
                this.loading = true;
                try {
                    const updatedPayroll = await this.$store.dispatch(`salary/${action}`, payroll);
                    this.showMessage('Сохранено');
                    for (const field in updatedPayroll) {
                        if (Object.prototype.hasOwnProperty.call(updatedPayroll, field) && Object.prototype.hasOwnProperty.call(payroll, field)) {
                            payroll[field] = updatedPayroll[field];
                        }
                    }
                    payroll.editMode = false;
                    this.uneditedPayrolls.splice(this.uneditedPayrolls.findIndex(payroll => payroll.id === +payrollId), 1);
                    if (payroll.isNew) {
                        payroll.isNew = false;
                    }
                } catch (error) {
                    payroll.errors = JSON.parse(error.message);
                } finally {
                    this.loading = false;
                }
            }
        },
        showMessage(message) {
            this.$notify({ title: 'Успешно', message, type: 'success' });
        },
        async getPayrolls() {
            this.loading = true;
            const requestParams = {
                size: this.pages.size,
                page: this.pages.current,
                filter: this.filter
            };
            try {
                const response = await this.$store.dispatch('salary/getPayrolls', requestParams);
                this.payrolls = response.data.map(payroll => Object.assign({}, payroll, { editMode: false }));
                this.pages.current = response.meta.current_page;
                this.pages.total = response.meta.total;
            } catch (error) {
                console.log(error.message);
            } finally {
                this.loading = false;
            }
        },
        handleDeleteButtonClick(payrollId) {
            const payroll = this.findPayroll(payrollId);
            if (payroll === undefined) {
                return;
            }
            this.$confirm('Удалить выбранную запись?', 'Внимание', {
                confirmButtonText: 'Да',
                cancelButtonText: 'Нет',
                type: '',
                center: true
            }).then(() => {
                this.deletePayroll(payrollId);
            }).catch(() => { });
        },
        async deletePayroll(payrollId) {
            this.loading = true;
            try {
                await this.$store.dispatch('salary/deletePayroll', payrollId);
                this.showMessage('Удалено');
                this.getPayrolls();
            } catch (error) {
                console.log(error.message);
            } finally {
                this.loading = false;
            }
        }
    },
    mounted() {
        this.getPayrolls();
    }
};
</script>
